import { useEffect, useRef, useState } from 'react';
import { TextField, type TextFieldProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatBelgianDate, parseBelgianDate } from '@/utils/date';

type BelgianDateFieldProps = Omit<TextFieldProps, 'onChange' | 'type' | 'value'> & {
  value?: string | null;
  onChange: (isoDate: string) => void;
};

/**
 * A text field for entering dates in Belgian `dd/MM/yyyy` display format
 * while emitting ISO `yyyy-MM-dd` values through `onChange`.
 *
 * Tracks its own display string separately from the emitted ISO value so
 * partial input (e.g. only the day typed so far) isn't reformatted mid-edit.
 * Shows a format-error hint once the field has been touched and blurred with
 * an unparsable value, unless an external `error`/`helperText` is supplied.
 *
 * @param value - The current value as an ISO `yyyy-MM-dd` string, or `null`/`undefined` for empty.
 * @param onChange - Called with the parsed ISO date string (or `''` while unparsable).
 * @param error - External error flag, takes precedence over the internal format-error hint.
 * @param helperText - External helper text, takes precedence over the internal format-error hint.
 * @param onBlur - Additional blur handler; also used to mark the field as touched.
 * @param props - Remaining MUI `TextField` props, excluding `onChange`, `type`, and `value`.
 * @returns The Belgian-format date input field.
 */
export function BelgianDateField({ value, onChange, error, helperText, onBlur, ...props }: BelgianDateFieldProps) {
  const { t } = useTranslation();
  const normalizedValue = value ?? '';
  const [displayValue, setDisplayValue] = useState(() => formatBelgianDate(normalizedValue));
  const [touched, setTouched] = useState(false);
  const lastEmittedValue = useRef(normalizedValue);

  useEffect(() => {
    if (normalizedValue !== lastEmittedValue.current) {
      setDisplayValue(formatBelgianDate(normalizedValue));
      lastEmittedValue.current = normalizedValue;
      setTouched(false);
    }
  }, [normalizedValue]);

  const invalid = displayValue.length > 0 && parseBelgianDate(displayValue) === null;
  const showFormatError = touched && invalid && !error;

  return <TextField
    {...props}
    type="text"
    value={displayValue}
    placeholder={t('common.datePlaceholder')}
    error={Boolean(error) || showFormatError}
    helperText={helperText ?? (showFormatError ? t('common.invalidDateFormat') : undefined)}
    inputProps={{ ...props.inputProps, inputMode: 'numeric', maxLength: 10 }}
    onChange={(event) => {
      // Do not reconstruct the complete date while typing: that forces the caret
      // to the end and prevents users from editing only the day or month.
      const display = event.target.value.replace(/[^\d/]/g, '').slice(0, 10);
      const parsed = parseBelgianDate(display) ?? '';
      setDisplayValue(display);
      lastEmittedValue.current = parsed;
      onChange(parsed);
    }}
    onBlur={(event) => {
      setTouched(true);
      onBlur?.(event);
    }}
  />;
}
